import React, {useContext, useRef, useState} from 'react';
import {Context} from '../contexts/WordList';
import {fetchWords, updateWord} from '../helpers/localApi';
import {
  generateSentences,
  suggestTag,
  suggestTranslation,
} from '../helpers/externalApi';
import {useAiAvailable} from '../contexts/AiAvailability';

/*
 * Admin tool: bulk-fill missing word fields (transliteration, sample
 * sentences, tags) for the current language. Uses the same local API
 * (updateWord) as the word details drawer, so validation/normalization
 * stays identical to single-word edits.
 */
export const BulkEnrichPanel = () => {
  const [state, dispatch] = useContext(Context);
  const {language} = state;
  const {aiAvailable, aiChecking} = useAiAvailable();

  const [fillTranslit, setFillTranslit] = useState(true);
  const [fillSentences, setFillSentences] = useState(true);
  const [fillTag, setFillTag] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(true);

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const cancelRef = useRef(false);

  const langName = language === 'hebrew' ? 'Hebrew' : 'Farsi';
  const fieldsSelected = fillTranslit || fillSentences || fillTag;

  const handleCancel = () => {
    cancelRef.current = true;
  };

  const handleRun = async () => {
    if (running || !fieldsSelected || !aiAvailable) return;

    setRunning(true);
    cancelRef.current = false;
    setProgress({
      phase: 'loading',
      current: 0,
      total: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
      wordEn: '',
    });

    let words;
    try {
      words = await fetchWords({language, orderBy: 'wordEn'});
    } catch (error) {
      setProgress({
        phase: 'error',
        message: `Failed to load words: ${error.message}`,
        current: 0,
        total: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
        wordEn: '',
      });
      setRunning(false);
      return;
    }

    const total = words.length;
    let updated = 0;
    let skipped = 0;
    let failed = 0;

    for (let index = 0; index < words.length; index++) {
      if (cancelRef.current) break;

      const word = words[index];
      setProgress({
        phase: 'running',
        current: index + 1,
        total,
        updated,
        skipped,
        failed,
        wordEn: word.wordEn,
      });

      try {
        const updates = {};

        if (fillTranslit && (!onlyMissing || !word.transliteration)) {
          const result = await suggestTranslation({
            word: word.wordFa,
            fromLang: langName,
            toLang: 'English',
          });
          if (result && result.transliteration) {
            updates.transliteration = result.transliteration;
          }
        }

        if (cancelRef.current) break;

        if (fillSentences && (!onlyMissing || !word.sentences)) {
          const sentences = await generateSentences({
            wordEn: word.wordEn,
            wordFa: word.wordFa,
            language,
          });
          if (Array.isArray(sentences) && sentences.length > 0) {
            updates.sentences = JSON.stringify(sentences);
          }
        }

        if (cancelRef.current) break;

        const currentTags = Array.isArray(word.tags) ? word.tags : [];
        if (fillTag && (!onlyMissing || currentTags.length === 0)) {
          const tag = await suggestTag({
            wordEn: word.wordEn,
            wordFa: word.wordFa,
          });
          if (tag && !currentTags.includes(tag)) {
            updates.tags = [...currentTags, tag];
          }
        }

        if (Object.keys(updates).length === 0) {
          skipped += 1;
        } else {
          const saved = await updateWord({
            language,
            id: word.key,
            ...updates,
          });
          // Keep the word list context in sync (EDIT_WORD patches
          // sentences/tags only when provided).
          dispatch({
            type: 'EDIT_WORD',
            payload: {
              key: saved.key,
              wordEn: saved.wordEn,
              wordFa: saved.wordFa,
              mediaUrl: saved.mediaUrl,
              transliteration: saved.transliteration,
              sentences: saved.sentences,
              tags: saved.tags,
            },
          });
          updated += 1;
        }
      } catch (error) {
        failed += 1;
        // eslint-disable-next-line no-console
        console.warn('Bulk enrich failed for word:', word.wordEn, error);
      }
    }

    setProgress({
      phase: cancelRef.current ? 'cancelled' : 'done',
      current: total,
      total,
      updated,
      skipped,
      failed,
      wordEn: '',
    });
    setRunning(false);
  };

  const percent = progress && progress.total > 0 ?
    Math.round((progress.current / progress.total) * 100) :
    0;

  return (
    <section
      className="admin-form bulk-enrich-panel"
      aria-label="Bulk word enrichment"
    >
      <h3 className="bulk-enrich-title">Bulk Word Enrichment</h3>
      <p className="bulk-enrich-subtitle">
        Fill word fields for <strong>{langName}</strong> using the AI service.
        Words are saved through the same API used for single-word edits.
      </p>

      {!aiChecking && !aiAvailable && (
        <div className="bulk-enrich-offline" role="note">
          AI service is offline — configure the host above and re-check the
          service to enable bulk enrichment.
        </div>
      )}

      <div className="bulk-enrich-options">
        <label className="bulk-option-toggle">
          <input
            type="checkbox"
            checked={fillTranslit}
            onChange={(e) => setFillTranslit(e.target.checked)}
            disabled={running || !aiAvailable}
          />
          <span>Transliteration</span>
        </label>
        <label className="bulk-option-toggle">
          <input
            type="checkbox"
            checked={fillSentences}
            onChange={(e) => setFillSentences(e.target.checked)}
            disabled={running || !aiAvailable}
          />
          <span>Sample sentences</span>
        </label>
        <label className="bulk-option-toggle">
          <input
            type="checkbox"
            checked={fillTag}
            onChange={(e) => setFillTag(e.target.checked)}
            disabled={running || !aiAvailable}
          />
          <span>Tag</span>
        </label>
        <label className="bulk-option-toggle">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            disabled={running || !aiAvailable}
          />
          <span>Only fill empty fields</span>
        </label>
      </div>

      <div className="bulk-enrich-actions">
        <button
          type="button"
          className="btn btn-ai"
          onClick={handleRun}
          disabled={running || !fieldsSelected || !aiAvailable}
        >
          {running ? '⏳ Running...' : '✨ Start Enrichment'}
        </button>
        {running && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancel}
          >
            Cancel
          </button>
        )}
      </div>

      {progress && (running || progress.phase !== 'loading') && (
        <div className="bulk-save-progress" role="status" aria-live="polite">
          {progress.phase === 'error' ? (
            <div className="bulk-save-progress-meta">{progress.message}</div>
          ) : (
            <>
              <div className="bulk-save-progress-row">
                <strong>
                  {progress.phase === 'running' &&
                    `Processing ${progress.current}/${progress.total}`}
                  {progress.phase === 'done' &&
                    `Finished: ${progress.updated} updated`}
                  {progress.phase === 'cancelled' &&
                    `Cancelled: ${progress.updated} updated so far`}
                </strong>
                <span>{percent}%</span>
              </div>
              <div className="bulk-save-progress-track">
                <div
                  className="bulk-save-progress-fill"
                  style={{width: `${percent}%`}}
                />
              </div>
              <div className="bulk-save-progress-meta">
                {progress.phase === 'running' && progress.wordEn &&
                  `Current: ${progress.wordEn} · `}
                Updated: {progress.updated} · Skipped: {progress.skipped}
                {progress.failed > 0 && ` · Failed: ${progress.failed}`}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
